import { amountPerLabel, amountPeriodLabel } from './amount-per.js';
/** 기둥 이름. 카드 · 탭 · 상세 · 콘솔이 전부 이 말을 쓴다. */
export const SUPPORT_VIEW_LABEL = {
    grant: '자금·바우처',
    loan: '융자·보증',
    facility: '시설·입주·공간',
    program: '프로그램',
};
/** 탭 줄의 짧은 이름표. 모바일에서 온전한 이름 넷이 한 줄을 넘쳤다 (2026-09-24). 카드 · 배지 · 칩은 `SUPPORT_VIEW_LABEL`. */
export const SUPPORT_TAB_LABEL = {
    grant: '자금',
    loan: '융자',
    facility: '시설',
    program: '프로그램',
};
export const PROGRAM_KIND_LABEL = {
    learning: '교육·컨설팅·멘토링',
    networking: '네트워킹·행사',
    contest: '공모전·대회',
    fair: '전시·박람회',
    sales: '판로·마케팅',
    overseas: '수출·해외진출',
    other: '기타',
};
export const FACILITY_KIND_LABEL = {
    space: '공간',
    equipment: '장비',
    incubation: '입주',
};
const KIND_LABEL = { ...PROGRAM_KIND_LABEL, ...FACILITY_KIND_LABEL };
/**
 * 금액을 **읽는 단위로** 줄인다. 「50,000,000원」보다 「5,000만원」이 빨리 읽힌다.
 *
 * 반올림하지 않는다 — 1,250만원을 「1,300만원」으로 보여주면 우리가 숫자를
 * 지어낸 것이다. 딱 떨어지지 않으면 아래 단위까지 적는다.
 */
export function shortAmount(won) {
    const 억 = Math.floor(won / 100_000_000);
    const 만 = Math.floor((won % 100_000_000) / 10_000);
    const 원 = won % 10_000;
    /*
      억 단위에도 자리쉼표를 찍는다. 기업당 한도만 다룰 때는 두 자리를 넘을 일이
      없어 드러나지 않았는데(실측 최대 20억), **총재원을 싣기 시작하면서** 네
      자리가 나왔다 — 「지원규모 3,300억원」이 「3300억원」으로 찍힌다.
    */
    if (억 > 0) {
        const 억Text = 억.toLocaleString('ko-KR');
        // 천만 단위로 딱 떨어지면 「2억 2,000만원」보다 「2.2억원」이 한국어로 자연스럽다.
        if (만 > 0 && 만 % 1_000 === 0)
            return `${억Text}.${만 / 1_000}억원`;
        return 만 > 0 ? `${억Text}억 ${만.toLocaleString('ko-KR')}만원` : `${억Text}억원`;
    }
    if (만 > 0)
        return 원 > 0 ? `${만.toLocaleString('ko-KR')}만 ${원.toLocaleString('ko-KR')}원` : `${만.toLocaleString('ko-KR')}만원`;
    return `${won.toLocaleString('ko-KR')}원`;
}
/** 0.01%p 정수를 사람이 읽는 퍼센트로. 250 → `2.5%`. */
export const ratePercent = (v) => `${(v / 100).toFixed(2).replace(/\.?0+$/, '')}%`;
/**
 * 카드 한 줄. **유형마다 다른 질문에 답한다.**
 *
 * 값이 없으면 `null` 을 낸다 — 「금액 미정」 같은 빈 말을 채우지 않는다.
 * 해석이 안 끝났는지 원문에 없었는지를 카드가 구별할 수 없기 때문이다.
 */
/** 총재원 ÷ 머릿수. 만원 단위로 반올림 — 「약」을 붙여 말하는 추정치라 자릿수를 줄인다. */
export function perHead(fundTotal, capacity) {
    return Math.round(fundTotal / capacity / 10_000) * 10_000;
}
export function supportHeadline(key, s) {
    const parts = [];
    if (key === 'program') {
        const kinds = (s.kinds ?? []).map((k) => KIND_LABEL[k] ?? k).filter(Boolean);
        // 종류가 없으면 이름으로 물러선다 — 이름이 「글로벌 오피스아워」라고 말한다.
        if (kinds.length === 0)
            return nameLine(s);
        return kinds.slice(0, 3).join(' · ') + (kinds.length > 3 ? ' 외' : '');
    }
    /*
      **시설은 「어디서, 얼마나 오래」에 답한다.** 갈래(공간 · 장비 · 입주) 다음에 기간과
      임대료 감면. 셋 다 없으면 이름으로 물러선다.
    */
    if (key === 'facility') {
        const kinds = (s.kinds ?? []).map((k) => KIND_LABEL[k] ?? k).filter(Boolean);
        const bits = [];
        if (kinds.length > 0)
            bits.push(kinds.slice(0, 2).join(' · '));
        if (typeof s.durationMonths === 'number' && s.durationMonths > 0)
            bits.push(`최대 ${monthsLabel(s.durationMonths)}`);
        if (typeof s.rentDiscount === 'number')
            bits.push(`임대료 ${s.rentDiscount}% 감면`);
        return bits.length > 0 ? bits.join(' · ') : nameLine(s);
    }
    /*
      **분모가 있으면 「최대」 대신 그 말을 쓴다** (`0028`).
  
      「1인당 30만원」을 「최대 30만원」이라 쓰면 사람 수만큼 적게 말한다 —
      12개월 × 3명이면 실제로 1,080만원이다. 실측에서 모집중 공고의 기업당
      한도 2,392건 중 231건이 이렇게 나가고 있었다.
  
      「최대」를 앞에 덧붙이지 않는다. 「1인당 최대 30만원」은 한 사람이 30만원을
      다 못 받을 수도 있다는 뜻으로 읽히는데, 원문이 말한 것은 그게 아니다.
    */
    if (typeof s.maxAmount === 'number') {
        /*
          기간 축도 같은 자리에 선다 (`0029`) — 「1인당 월 30만원」. 「월 60만원 ×
          최대 12개월」을 「최대 60만원」이라 쓰면 12분의 1만 말한다. 곱해서 720만원을
          쓰지 않는다 — 원문에 없는 숫자다.
        */
        const per = amountPerLabel(s.amountPer);
        const period = amountPeriodLabel(s.amountPeriod);
        const head = [per, period].filter(Boolean).join(' ');
        /*
          **조건별 구간이면 범위로 말한다** (`0030`). 「최대 15억원」은 1,000만불
          수출 업체의 숫자다. 대다수 신청자의 숫자(1,500만원)와 함께 써야 거짓이
          아니다. 위·아래가 같으면 구간이 아니라 그냥 한도다.
        */
        const tiered = typeof s.tierLo === 'number' && typeof s.tierHi === 'number' && s.tierLo < s.tierHi;
        /*
          **월 금액에 기간이 붙어 있으면 합계까지 말한다.** 「월 180만원 · 최대 2개월」을 「월 180만원」
          으로만 두면 독자가 받을 돈의 절반만 말한 것이다(실측 d22ff26f). 곱은 원문의 두 숫자에서
          바로 나오므로 지어낸 값이 아니다 — 「월 180만원 × 2개월 · 최대 360만원」.
        */
        const months = period === '월' && typeof s.durationMonths === 'number' && s.durationMonths > 0 ? s.durationMonths : null;
        if (tiered) {
            parts.push(`조건별 ${head ? `${head} ` : ''}${shortAmount(s.tierLo)}~${shortAmount(s.tierHi)}`);
        }
        else if (months) {
            parts.push(`${head} ${shortAmount(s.maxAmount)} × ${months}개월 · 최대 ${shortAmount(s.maxAmount * months)}`);
        }
        else {
            parts.push(head ? `${head} ${shortAmount(s.maxAmount)}` : `최대 ${shortAmount(s.maxAmount)}`);
        }
    }
    /*
      **총재원은 기업당 한도가 없을 때만, 그리고 「총」을 붙여서 말한다.**
  
      예전엔 이런 공고가 금액 칸을 통째로 비웠다. 충북 바이오 오픈이노베이션
      공고가 「총 140백만원 한도 내 기업별 차등 지원 ※ 자부담 10% 별도」인데
      화면은 자부담 10%만 말했다 — 1억 4,000만원을 네 곳이 나눠 갖는다는,
      독자가 가장 알고 싶은 사실이 빠졌다.
  
      처음엔 「총 1억 4,000만원 · 4개사」라고 총액을 말했고 나눗셈은 안 했다 — 「차등
      지원」이라 균등이 아니고 원문에 없는 숫자라서. 그런데 「총 182억원」은 독자에게
      아무 뜻이 없고 오히려 182억을 한 회사 몫으로 읽힌다(실측 3f17a4b6). 그래서 뒤집었다:
      **총액은 빼고 기업당 몫만 「약」을 달아 말한다.** 머릿수를 모르면 아무 말도 안 한다.
    */
    else if (typeof s.fundTotal === 'number' && typeof s.capacity === 'number' && s.capacity > 0) {
        // **총액은 말하지 않는다.** 「예산 182억」은 독자에게 아무 뜻이 없다 — 내 몫이 얼마냐가
        // 전부다 (2026-09-12 소유자 결정). 머릿수가 있어 몫을 셈할 수 있을 때만 그 몫을 말한다.
        parts.push(`기업당 약 ${shortAmount(perHead(s.fundTotal, s.capacity))} · ${s.capacity}개사`);
    }
    /*
      **실비 지원은 숫자가 없어도 「얼마나」에 대한 답이다.** 「견적서 낮은 쪽
      금액을 전액 지원」은 상한이 없는 것이지 금액 정보가 없는 게 아니다.
      「정보 없음」이라고 쓰면 진짜 보조금이 빈 칸으로 보인다.
    */
    else if (s.actualCost === true) {
        parts.push('실비 지원');
    }
    /*
      **투자는 「지분」이라고 말한다.** 갚지 않는다는 점에서 보조금과 같지만
      회사 일부를 가져간다. 그 말을 빼면 화면이 공짜 돈이라고 거짓말한다.
    */
    if (key === 'grant' && typeof s.equityMax === 'number') {
        parts.push(`투자 ${shortAmount(s.equityMax)}(지분)`);
    }
    if (key === 'loan') {
        // 두 종류의 금리가 다르면 범위로 낸다. 낮은 쪽만 보여주면 유리하게 읽힌다.
        if (typeof s.rateMin === 'number') {
            parts.push(typeof s.rateMax === 'number' && s.rateMax !== s.rateMin
                ? `연 ${ratePercent(s.rateMin)}~${ratePercent(s.rateMax)}`
                : `연 ${ratePercent(s.rateMin)}`);
        }
        else if (typeof s.interestSubsidy === 'number') {
            // 금리 자체가 아니라 깎아주는 폭이다. 「금리 2.3%」로 읽히면 정반대다.
            parts.push(`금리 ${ratePercent(s.interestSubsidy)}p 감면`);
        }
        if (typeof s.guaranteeFeeRate === 'number')
            parts.push(`보증료 ${ratePercent(s.guaranteeFeeRate)}`);
        if (typeof s.guaranteeRatio === 'number')
            parts.push(`보증 ${s.guaranteeRatio}%`);
    }
    if (key === 'grant' && typeof s.ratio === 'number')
        parts.push(`${s.ratio}% 지원`);
    /*
      **자부담도 구간이면 범위로 말한다.** OK FTA 컨설팅은 매출 구간별로 0~40% 인데
      최댓값 하나(40%)로 쓰면 대다수(50억 이하, 0%)에게 거짓이다.
    */
    if (typeof s.selfPay === 'number') {
        const tiered = typeof s.selfPayLo === 'number' && typeof s.selfPayHi === 'number' && s.selfPayLo < s.selfPayHi;
        parts.push(tiered ? `자부담 ${s.selfPayLo}~${s.selfPayHi}%` : `자부담 ${s.selfPay}%`);
    }
    if (parts.length > 0)
        return parts.join(' · ');
    /*
      **숫자가 없으면 이름을 보여준다.** 이름조차 없으면 아무 말도 하지 않는다.
  
      「1개 항목」 같은 문구를 쓰지 않는다 — 자리를 차지하면서 독자에게 아무것도
      안 알려주고, 값이 있는 카드와 없는 카드를 같은 무게로 보이게 한다. 할 말이
      없으면 안 하는 게 낫다.
    */
    return nameLine(s);
}
/** 「12개월」이 아니라 「1년」. 시설 기간에 쓴다. */
function monthsLabel(months) {
    if (months % 12 === 0 && months >= 12)
        return `${months / 12}년`;
    return `${months}개월`;
}
/**
 * 이름 목록을 한 줄로. 길면 잘라내고 「외」를 붙인다.
 *
 * 예산이 22자인 이유: 카드 첫 열이 208px 다. 「블렌디드 파이낸싱(100% TIPS
 * 연계) · 사업화 지원금」처럼 41자가 들어가면 네 줄로 접혀 카드가 무너진다.
 * 첫 이름은 길어도 통째로 넣는다 — 이름을 잘라 쓰면 그건 다른 이름이 된다.
 */
function nameLine(s) {
    const names = (s.names ?? []).filter(Boolean);
    if (names.length === 0)
        return null;
    const shown = [];
    let used = 0;
    for (const n of names) {
        if (shown.length > 0 && used + n.length > 22)
            break;
        shown.push(n);
        used += n.length;
    }
    return shown.join(' · ') + (shown.length < names.length ? ' 외' : '');
}
//# sourceMappingURL=grant-support-view.js.map